<script lang="ts">
  import { Check, Plus, Search, X, Trash2, Wallet, ChevronRight } from "lucide-svelte";
  import type { WalletCard } from "../wallet/types";
  import { recordCardRequest, submitCardRequest, type CustomCard } from "./profile";
  import Button from "./ui/Button.svelte";
  import Card from "./ui/Card.svelte";
  import CardContent from "./ui/CardContent.svelte";
  import CardHeader from "./ui/CardHeader.svelte";
  import CardThumb from "./CardThumb.svelte";
  import { cn } from "./lib/utils";

  export let selectedCards: WalletCard[];
  export let allCatalog: WalletCard[];
  export let customCards: CustomCard[];
  export let onChange: (next: { selectedIds: string[]; customCards: CustomCard[] }) => void;
  export let onView: (card: WalletCard) => void = () => {};

  let panelOpen = false;
  let cardSearch = "";
  let showCustomForm = false;
  let custom = {
    name: "",
    issuer: "",
    level: "白金卡",
    annualFeeText: "",
    email: "",
    note: ""
  };

  $: selectedIds = new Set(selectedCards.map(c => c.id));

  $: filteredCandidates = (() => {
    const q = cardSearch.trim().toLowerCase();
    const known = allCatalog.filter(c => !selectedIds.has(c.id));
    if (!q) return known.slice(0, 18);
    return known
      .filter(c => {
        const hay = [c.name, c.shortName, c.issuer, ...(c.aliases || [])].join(" ").toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 18);
  })();

  $: canSaveCustom = custom.name.trim().length > 0 && custom.issuer.trim().length > 0;

  function addCard(id: string) {
    if (selectedIds.has(id)) return;
    onChange({ selectedIds: [...selectedIds, id], customCards });
  }

  function removeCard(id: string) {
    const newIds = [...selectedIds].filter(x => x !== id);
    const newCustom = customCards.filter(c => c.id !== id);
    onChange({ selectedIds: newIds, customCards: newCustom });
  }

  function startCustomForm() {
    showCustomForm = true;
    custom = {
      name: cardSearch,
      issuer: "",
      level: "白金卡",
      annualFeeText: "",
      email: "",
      note: ""
    };
  }

  function cancelCustomForm() {
    showCustomForm = false;
  }

  async function saveCustomCard() {
    if (!canSaveCustom) return;
    const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const newCard: CustomCard = {
      id,
      name: custom.name.trim(),
      issuer: custom.issuer.trim(),
      level: custom.level.trim() || "信用卡",
      annualFeeText: custom.annualFeeText.trim() || "年费用户自录",
      reportedAt: new Date().toISOString()
    };
    const nextCustom = [...customCards, newCard];
    onChange({ selectedIds: [...selectedIds, id], customCards: nextCustom });

    const req = recordCardRequest({
      cardName: newCard.name,
      issuer: newCard.issuer,
      level: newCard.level,
      annualFeeText: newCard.annualFeeText,
      email: custom.email.trim(),
      note: custom.note.trim()
    });
    submitCardRequest(req);
    showCustomForm = false;
    cardSearch = "";
  }
</script>

<Card class="mb-5">
  <CardHeader class="pb-3">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
        <Wallet class="size-4" />
        <span>你的卡包（{selectedCards.length}）</span>
      </div>
      <Button
        variant="ghost"
        size="sm"
        on:click={() => {
          panelOpen = !panelOpen;
          if (!panelOpen) showCustomForm = false;
        }}
      >
        {#if panelOpen}收起{:else}<Plus class="mr-1 size-3.5" />添加卡{/if}
      </Button>
    </div>
  </CardHeader>
  <CardContent>
    {#if selectedCards.length > 0}
      <ul class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {#each selectedCards as card}
          <li class="group flex items-center gap-3 rounded-xl border bg-secondary/60 p-2.5 transition-all hover:border-primary/40 hover:bg-secondary">
            <button
              type="button"
              class="focus-amber flex min-w-0 flex-1 items-center gap-3 text-left"
              aria-label={`查看 ${card.shortName || card.name} 详情`}
              on:click={() => onView(card)}
            >
              <CardThumb {card} size="sm" />
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium">{card.shortName || card.name}</div>
                <div class="mt-0.5 truncate text-[11px] text-muted-foreground">
                  {card.issuer} · {card.level}
                </div>
              </div>
              <ChevronRight class="size-3.5 text-muted-foreground/40 group-hover:text-muted-foreground" />
            </button>
            <button
              type="button"
              class="focus-amber shrink-0 rounded-md p-1.5 text-muted-foreground/60 hover:bg-foreground/5 hover:text-destructive"
              aria-label="移除"
              on:click|stopPropagation={() => removeCard(card.id)}
            >
              <Trash2 class="size-3.5" />
            </button>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="text-sm text-muted-foreground">卡包是空的。点上方「添加卡」加几张。</p>
    {/if}

    {#if panelOpen}
      <div class="mt-4 border-t pt-4 space-y-3">
        {#if !showCustomForm}
          <div class="relative">
            <Search class="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="搜卡名、发卡行、别名…"
              bind:value={cardSearch}
              class="w-full rounded-md border bg-background px-9 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {#if cardSearch}
              <button
                type="button"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                on:click={() => (cardSearch = "")}
              >
                <X class="size-4" />
              </button>
            {/if}
          </div>

          {#if filteredCandidates.length > 0}
            <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {#each filteredCandidates as card}
                <button
                  type="button"
                  class="flex flex-col gap-0.5 rounded-md border bg-card px-3 py-2 text-left text-sm transition-colors hover:border-primary"
                  on:click={() => addCard(card.id)}
                >
                  <span class="truncate font-medium">{card.shortName || card.name}</span>
                  <span class="truncate text-[11px] text-muted-foreground">
                    {card.issuer} · {card.level}
                  </span>
                  <span class="mt-1 inline-flex items-center gap-1 text-[10px] text-primary">
                    <Plus class="size-3" />
                    加入卡包
                  </span>
                </button>
              {/each}
            </div>
          {:else if cardSearch}
            <p class="rounded-md border border-dashed bg-card/40 px-4 py-3 text-center text-sm">
              「{cardSearch}」没找到。
            </p>
          {/if}

          <Button variant="outline" size="sm" on:click={startCustomForm} class="w-full">
            <Plus class="mr-1 size-3.5" />
            找不到？自己录一张
          </Button>
        {:else}
          <div class="rounded-md border-2 border-primary/40 bg-card p-4 space-y-3">
            <p class="text-sm font-semibold">自录一张卡</p>
            <p class="text-xs text-muted-foreground">
              会按你填的资料加进卡包；同时提交收录请求，按请求频次正式录。
            </p>
            <label class="block">
              <span class="text-xs text-muted-foreground">卡名称（必填）</span>
              <input
                type="text"
                bind:value={custom.name}
                placeholder="如：中信i白金"
                class="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <label class="block">
              <span class="text-xs text-muted-foreground">发卡行（必填）</span>
              <input
                type="text"
                bind:value={custom.issuer}
                placeholder="如：中信银行"
                class="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <div class="grid grid-cols-2 gap-2">
              <label class="block">
                <span class="text-xs text-muted-foreground">等级</span>
                <input
                  type="text"
                  bind:value={custom.level}
                  placeholder="如：白金卡"
                  class="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
              <label class="block">
                <span class="text-xs text-muted-foreground">年费</span>
                <input
                  type="text"
                  bind:value={custom.annualFeeText}
                  placeholder="¥3600/年"
                  class="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>
            </div>
            <label class="block">
              <span class="text-xs text-muted-foreground">邮箱（选填 · 只用于按收录请求频次排优先级）</span>
              <input
                type="email"
                bind:value={custom.email}
                placeholder="you@example.com"
                class="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
            <div class="flex gap-2 pt-1">
              <Button variant="outline" size="sm" on:click={cancelCustomForm} class="flex-1">取消</Button>
              <Button size="sm" on:click={saveCustomCard} disabled={!canSaveCustom} class="flex-[2]">
                <Check class="mr-1 size-3.5" />
                保存并提交
              </Button>
            </div>
          </div>
        {/if}
      </div>
    {/if}
  </CardContent>
</Card>
